import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import {
  Modal,
  RateLimitInline,
} from '@qh/shared';
import {
  ApiError,
  RateLimitError,
  candidateApi,
  type MasterDataRef,
  type ResumeListItem,
} from '../../lib/apiClient';
import { formFromParsed, type ProfileFormState } from './profileFormShape';

/**
 * Re-parse picker. Lets the candidate run AI parse against any of their existing
 * resumes (analysis §5.3 — Work History card top-right affordance) and merge
 * work_history / education / skills back into the My Profile form. The candidate
 * still has to click Save to persist; we don't auto-PATCH.
 *
 * The re-parse hits the candidate-keyed throttle bucket (5/h). We surface the
 * countdown inline so the candidate isn't surprised by a 429.
 */
export function ReParseModal({
  open,
  onClose,
  onParsed,
  currentForm,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Fired once the parse + skill-name hydration finishes. `hydratedSkills`
   * carries the (id, name, slug) for every matched_skill_id so the receiver can
   * seed the SkillPicker catalog and chips render names instead of raw UUIDs.
   * The hydration is best-effort — a network failure leaves `hydratedSkills`
   * empty and the picker falls back to its existing search-driven hydration.
   */
  onParsed: (next: ProfileFormState, hydratedSkills: MasterDataRef[]) => void;
  currentForm: ProfileFormState;
}) {
  const [resumes, setResumes] = useState<ResumeListItem[] | null>(null);
  const [resumeId, setResumeId] = useState<string>('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setResumes(null);
    setLoadError(null);
    setParseError(null);
    candidateApi.resumes
      .list()
      .then((rows) => {
        if (cancelled) return;
        setResumes(rows);
        const primary = rows.find((r) => r.is_primary);
        setResumeId(primary?.id ?? rows[0]?.id ?? '');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Could not load your resumes.');
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function handleParse() {
    if (!resumeId) return;
    setParsing(true);
    setParseError(null);
    setRateLimitedUntil(null);
    try {
      const result = await candidateApi.profile.parseExisting(resumeId);
      // Merge: keep the form's user-set preferences (salary, region, toggles), but
      // overwrite the parser-derived sections (personal details, work history, education,
      // skills, summary). Job preferences are NEVER pulled from the resume.
      const parsedForm = formFromParsed(result.parsed, result.matched_skill_ids);
      const merged: ProfileFormState = {
        ...parsedForm,
        // Preserve fields that can't come from a resume parse:
        experience_level_id: currentForm.experience_level_id,
        notice_period_id: currentForm.notice_period_id,
        expected_salary_min: currentForm.expected_salary_min,
        expected_salary_max: currentForm.expected_salary_max,
        expected_salary_currency_id: currentForm.expected_salary_currency_id,
        expected_salary_pay_period_id: currentForm.expected_salary_pay_period_id,
        willing_to_relocate: currentForm.willing_to_relocate,
        // Declaration consents survive a re-parse — they're attestations by the
        // candidate, not data extracted from the resume.
        rtr_consent: currentForm.rtr_consent,
        fraud_declaration: currentForm.fraud_declaration,
      };
      // Hydrate names for every matched skill id so chips render names rather than
      // UUIDs. Best-effort: a failure here doesn't block the parse — the picker
      // falls back to search-driven hydration if the user types into it.
      let hydratedSkills: MasterDataRef[] = [];
      if (result.matched_skill_ids.length > 0) {
        try {
          hydratedSkills = await candidateApi.publicSkillsByIds(result.matched_skill_ids);
        } catch {
          hydratedSkills = [];
        }
      }
      onParsed(merged, hydratedSkills);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
      } else if (err instanceof ApiError) {
        setParseError(err.message);
      } else {
        setParseError('Could not re-parse the resume. Please try again.');
      }
    } finally {
      setParsing(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Re-parse profile from a resume"
      description="Pick one of your resumes — AI will overwrite Personal, Work History, Education, Skills, and Summary on the form. You still have to click Save to keep the changes."
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={parsing}
            className="inline-flex items-center rounded-2xl border border-gray-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-brand-muted hover:text-brand-primary hover:border-brand-primary disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleParse}
            disabled={
              !resumeId ||
              parsing ||
              (rateLimitedUntil !== null && rateLimitedUntil > Date.now())
            }
            className="inline-flex items-center gap-1.5 rounded-2xl bg-brand-primary px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white hover:bg-brand-deep disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
          >
            {parsing ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Parsing…
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                Parse
              </>
            )}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {loadError && <p className="text-sm text-red-700 font-medium">{loadError}</p>}
        {!resumes && !loadError && (
          <p className="text-sm text-brand-muted flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-brand-accent" />
            Loading your resumes…
          </p>
        )}
        {resumes && resumes.length === 0 && (
          <p className="text-sm text-brand-muted">
            You don't have any resumes yet. Upload one from My Resumes to enable re-parse.
          </p>
        )}
        {resumes && resumes.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-brand-muted">
              Choose a resume
            </p>
            <ul className="rounded-2xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
              {resumes.map((r) => (
                <li key={r.id}>
                  <label
                    className={`flex items-center gap-3 px-4 py-3 text-sm cursor-pointer ${
                      r.id === resumeId ? 'bg-brand-secondary/30' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reparse-resume"
                      value={r.id}
                      checked={r.id === resumeId}
                      onChange={() => setResumeId(r.id)}
                      className="accent-brand-primary"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block font-bold text-brand-primary truncate">
                        {r.original_filename}
                      </span>
                      <span className="block text-xs text-brand-muted">
                        {r.file_type}
                        {r.is_primary && ' · Primary'}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
        <RateLimitInline deadlineMs={rateLimitedUntil} />
        {parseError && <p className="text-sm text-red-700 font-medium">{parseError}</p>}
      </div>
    </Modal>
  );
}
